import React, { useState } from "react";
import { api } from "../../lib/api";
import { useFetch, act } from "../../lib/useFetch";
import { useAuth } from "../../lib/auth";
import { REVIEW_FILTERS, REVIEW_STATUS_LABELS } from "../../lib/constants";
import { fmtDateTime } from "../../lib/format";
import FilterPills from "./FilterPills";
import ReviewDialog from "./ReviewDialog";
import { Toggle } from "./AdminDialog";
import { Check, EyeOff, Pencil, Trash2 } from "lucide-react";

const btn = "btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1";

export const Stars = ({ n }) => (
  <span className="text-gold">{"★".repeat(n)}<span className="text-gray-600">{"★".repeat(5 - n)}</span></span>
);

function ReviewCard({ r, onAction, onEdit }) {
  return (
    <div className="glass-cyan rounded-xl p-4" data-testid={`review-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">{r.author_name || "—"}</span>
        <Stars n={r.rating} />
        <span className="chip">{r.language}</span>
        <span className="text-xs text-gray-300" data-testid={`review-status-${r.id}`}>{REVIEW_STATUS_LABELS[r.status]}</span>
        <span className="text-xs text-gray-500 ml-auto">{fmtDateTime(r.created_at)}</span>
      </div>
      <p className="text-sm text-gray-300 mt-2 whitespace-pre-line">{r.text}</p>
      <div className="flex gap-2 mt-3 flex-wrap">
        {r.status !== "published" && <button className={btn} onClick={() => onAction(r, "publish")} data-testid={`review-publish-${r.id}`}><Check size={12} /> Опубликовать</button>}
        {r.status !== "hidden" && <button className={btn} onClick={() => onAction(r, "hide")} data-testid={`review-hide-${r.id}`}><EyeOff size={12} /> Скрыть</button>}
        <button className={btn} onClick={() => onEdit(r)} data-testid={`review-edit-${r.id}`}><Pencil size={12} /></button>
        <button className={btn} onClick={() => onAction(r, "delete")} data-testid={`review-delete-${r.id}`}><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

export default function ReviewsPanel() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [data, reload] = useFetch(`/admin/reviews?status=${filter}`);
  const [editing, setEditing] = useState(null);

  const onAction = async (r, a) => {
    if (a === "delete") {
      if (!window.confirm("Удалить отзыв?")) return;
      await act(api.delete(`/admin/reviews/${r.id}`), "Отзыв удалён");
    } else {
      await act(api.post(`/admin/reviews/${r.id}/${a}`), a === "publish" ? "Отзыв опубликован" : "Отзыв скрыт");
    }
    reload();
  };
  const toggleAuto = async (enabled) => {
    await act(api.put("/admin/reviews-settings/auto-publish", { enabled }), "Настройка сохранена");
    reload();
  };
  const reviews = data?.reviews || [];

  return (
    <div className="glass rounded-2xl p-6" data-testid="panel-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-heading text-2xl font-bold">⭐ Отзывы</h3>
        {user.role_level >= 7 && <Toggle checked={data?.auto_publish} onChange={toggleAuto} label="Автопубликация новых отзывов" testid="reviews-auto-publish-toggle" />}
      </div>
      <FilterPills options={REVIEW_FILTERS} value={filter} onChange={setFilter} counts={data?.counts} testid="reviews-filter" />
      <div className="space-y-3 mt-5">
        {reviews.length === 0 ? <p className="text-gray-400">Отзывов нет.</p> : reviews.map((r) => <ReviewCard key={r.id} r={r} onAction={onAction} onEdit={setEditing} />)}
      </div>
      {editing && <ReviewDialog review={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}
